import PropTypes from 'prop-types';
import React from 'react';
import {observer, PropTypes as MobxPropTypes} from 'mobx-react';

import inject from '../../../../hoc/injectHoc';
import PlanStep from './components/PlanStep';
import CheckoutStep from './components/CheckoutStep';
import MediaPlayerStep from './components/MediaPlayerStep';
import AddonsHeader from './components/AddonsHeader';
import UpdatePlanAlert from '../updatePlanAlert/UpdatePlanAlert';
import CannotUpdatePlanAlert from '../cannotUpdatePlanAlert/CannotUpdatePlanAlert';
import {dashboardRoute} from '../../../../../components/routePaths';
import Link from '../../../../common/link/Link';
import FlowStepper from '../../../../../components/common/flowStepper/FlowStepper';

const PRODUCT_TYPE_PLAN = 'plan';
const PRODUCT_TYPE_MEDIA_PLAYER = 'mediaPlayer';

const STEP_KEY_PLANS = 'plans';
const STEP_KEY_MEDIA_PLAYERS = 'mediaPlayers';
const STEP_KEY_CHECKOUT = 'checkout';

const WORKFLOW_PLAN = [
  {
    label: 'Plans',
    key: STEP_KEY_PLANS,
    component: PlanStep,
  },
  {
    label: 'Addons',
    key: STEP_KEY_MEDIA_PLAYERS,
    component: MediaPlayerStep,
    header: AddonsHeader,
    headerProps: {
      description: 'Supercharge your indoor screens with a Project Content media player, turning any indoor screen into a sign.',
    },
    canEnter: (_store, props) => props.showAddons,
    isDisabled: (store) => !store.plan && !store.alaCarteCredits,
    onBack: (store) => {
      store.setPlan(null);
      store.setAlaCarteCredits(null);
    },
  },
  {
    label: 'Checkout',
    key: STEP_KEY_CHECKOUT,
    component: CheckoutStep,
    isDisabled: (store) => !store.plan && !store.alaCarteCredits,
    onBack: (store, targetStepKey) => {
      // if target step is plans (workflow does not include addons step)
      // reset plan
      if (targetStepKey === STEP_KEY_PLANS) {
        store.setPlan(null);
        store.setAlaCarteCredits(null);
      }
    },
  },
];
const WORKFLOW_MEDIA_PLAYER = [
  {
    label: 'Media Player',
    key: STEP_KEY_MEDIA_PLAYERS,
    component: MediaPlayerStep,
  },
  {
    label: 'Addons',
    key: STEP_KEY_PLANS,
    component: PlanStep,
    header: AddonsHeader,
    headerProps: {
      description: 'Make your Project Content media player shine with access to our library of over 1000 professionally designed templates.',
    },
    isDisabled: (store) => !store.mediaPlayerPlan,
  },
  {
    label: 'Checkout',
    key: STEP_KEY_CHECKOUT,
    component: CheckoutStep,
    isDisabled: (store) => !store.mediaPlayerPlan,
  },
];

/**
 * The FlowRenderer component.
 */
class FlowRenderer extends React.Component {
  /**
   * Triggered when the component is first mounted to the page.
   */
  componentDidMount() {
    const {purchaseFlowStore} = this.props;
    const initialStepKey = this.getInitialStepKey();

    if (initialStepKey) {
      purchaseFlowStore.setCurrentStep(initialStepKey);
    }
  }

  /**
   * Determine initial step based on store state
   *
   * @returns {?string}
   */
  getInitialStepKey = () => {
    const {purchaseFlowStore, productType, showAddons} = this.props;
    const steps = this.getSteps();

    // if productType is mediaPlayer
    //  and media player plan is preset
    //  and user has plan
    //  GOTO checkouts page
    if (purchaseFlowStore.mediaPlayerPlan && purchaseFlowStore.currentPlanId) {
      return STEP_KEY_CHECKOUT;
    }

    // if productType is mediaPlayer
    //  and media player plan is preset
    //  and user does not have a plan
    //  GOTO plans page
    if (productType === PRODUCT_TYPE_MEDIA_PLAYER && purchaseFlowStore.mediaPlayerPlan && !purchaseFlowStore.currentPlanId) {
      return STEP_KEY_PLANS;
    }

    // if productType is plan
    //  and addons step is available
    //  and plan is preset
    //  GOTO media player page
    if (productType === PRODUCT_TYPE_PLAN && showAddons && purchaseFlowStore.plan) {
      return STEP_KEY_MEDIA_PLAYERS;
    }

    // if productType is plan
    //  and addons step is NOT available
    //  and plan is preset
    //  GOTO checkout page
    if (productType === PRODUCT_TYPE_PLAN && !showAddons && purchaseFlowStore.plan) {
      return STEP_KEY_CHECKOUT;
    }

    if (steps.length) {
      return steps[0].key;
    }

    return undefined;
  }

  /**
   * Goto next step
   */
  nextStep = () => {
    const {purchaseFlowStore} = this.props;
    const steps = this.getSteps();
    const currentIndex = steps.findIndex((s) => s.key === purchaseFlowStore.currentStep);
    if (currentIndex < steps.length - 1) {
      purchaseFlowStore.setCurrentStep(steps[currentIndex + 1].key);
      window.scrollTo(0, 0);
    }
  }

  /**
   * Goto previous step
   */
  prevStep = () => {
    const {purchaseFlowStore} = this.props;
    const steps = this.getSteps();
    const currentIndex = steps.findIndex((s) => s.key === purchaseFlowStore.currentStep);
    if (currentIndex > 0) {
      const currentStepDef = steps[currentIndex];
      if (currentStepDef.onBack) {
        currentStepDef.onBack(purchaseFlowStore, steps[currentIndex - 1].key);
      }
      purchaseFlowStore.setCurrentStep(steps[currentIndex - 1].key);
      window.scrollTo(0, 0);
    }
  }

  /**
   * Render optional header for the current step.
   * @returns {{}}
   */
  renderStepHeader() {
    const {purchaseFlowStore} = this.props;
    const steps = this.getSteps();
    const currentStepDef = steps.find((s) => s.key === purchaseFlowStore.currentStep);
    const HeaderComponent = currentStepDef && currentStepDef.header;

    if (!HeaderComponent) {
      return null;
    }

    return <HeaderComponent nextStep={this.nextStep} prevStep={this.prevStep} {...(currentStepDef.headerProps || {})} />;
  }

  /**
   * Render step based on step from purchaseFlowStore
   * @returns {{}}
   */
  renderStep() {
    const {purchaseFlowStore} = this.props;

    const steps = this.getSteps();

    const currentStepDef = steps.find((s) => s.key === purchaseFlowStore.currentStep);
    const StepComponent = currentStepDef ? currentStepDef.component : undefined;

    if (!StepComponent) {
      return <div>Error rendering step</div>;
    }

    return <StepComponent nextStep={this.nextStep} prevStep={this.prevStep} />;
  }

  /**
   * Generate steps by productType
   *
   * @returns {[]}
   */
  getSteps() {
    let workflow = [];
    if (this.props.productType === PRODUCT_TYPE_PLAN) {
      workflow = WORKFLOW_PLAN;
    } else if (this.props.productType === PRODUCT_TYPE_MEDIA_PLAYER) {
      workflow = WORKFLOW_MEDIA_PLAYER;
    }

    return workflow
      .filter((step) => !step.canEnter || step.canEnter(this.props.purchaseFlowStore, this.props))
      .map((step) => ({
        ...step,
        disabled: step.isDisabled ? step.isDisabled(this.props.purchaseFlowStore) : false,
      }));
  }

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {purchaseFlowStore} = this.props;
    const {currentStep} = purchaseFlowStore;

    const steps = this.getSteps();

    const currentIndex = steps.findIndex((s) => s.key === currentStep);
    const previousStepDef = steps[currentIndex - 1];
    const isFirstStep = currentIndex === 0;

    const {canUpdatePlan, requiresPlanUpdate, cannotUpdatePlanMessage, updatePlanMessage} = purchaseFlowStore;

    return (<>
      <FlowStepper
        steps={steps}
        currentStep={currentStep}
        changeStep={(stepKey) => {
          purchaseFlowStore.setCurrentStep(stepKey);
        }}
        disablePreviousButton
      />
      <div className="container px-0 py-3">
        <div className="onboarding-content">
          <div>
            <div className="my-3">
              {isFirstStep
                ? (
                  <Link to={dashboardRoute} className="btn btn-light">
                    ← Back to Dashboard
                  </Link>
                )
                : (
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={this.prevStep}
                  >
                    ← {previousStepDef ? previousStepDef.label : 'Back' }
                  </button>
                )
              }
            </div>

            {!canUpdatePlan && <CannotUpdatePlanAlert message={cannotUpdatePlanMessage} />}
            {(requiresPlanUpdate && canUpdatePlan) && <UpdatePlanAlert message={updatePlanMessage} />}

            {this.renderStepHeader()}
            {this.renderStep()}
          </div>
        </div>
      </div>
    </>);
  }
}

FlowRenderer.propTypes = {
  productType: PropTypes.string,
  purchaseFlowStore: MobxPropTypes.observableObject,
  showAddons: PropTypes.bool,
};

FlowRenderer.defaultProps = {
  productType: PRODUCT_TYPE_PLAN,
  showAddons: false,
};

FlowRenderer.wrappedComponent = {};
FlowRenderer.wrappedComponent.propTypes = {
  purchaseFlowStore: MobxPropTypes.observableObject.isRequired,
};

export default inject(FlowRenderer)(
  observer(FlowRenderer)
);
